/**
 * Bundle Card Component
 *
 * @package
 */

import { useState, useEffect, memo } from '@wordpress/element';
import PropTypes from 'prop-types';
import { __ } from '@wordpress/i18n';
import { decodeHtmlEntities } from '../helpers/decodeHtmlEntities';
import { formatPrice, getPriceFromProduct } from '../helpers/priceHelpers';
import { cachedApiFetch, prefetchProducts } from '../helpers/useApiCache';

const BundleCard = ( {
	bundleData,
	config,
	isSelected,
	onSelect,
	onDeselect,
	onAutoSelect,
} ) => {
	const [ productsData, setProductsData ] = useState( [] );
	const [ totalPrice, setTotalPrice ] = useState( 0 );

	const featuredImageUrl = bundleData?.featured_media_url || null;

	useEffect( () => {
		if ( ! Array.isArray( bundleData?.acf?.selected_optionals ) ) {
			setProductsData( [] );
			setTotalPrice( 0 );
			return;
		}

		let cancelled = false;
		const productIds = bundleData.acf.selected_optionals;

		// One batch request seeds the shared cache; the per-product reads below hit it.
		prefetchProducts( productIds );

		const productPromises = productIds.map( productId =>
			cachedApiFetch( `/wp/v2/qcom_product/${ productId }` )
				.then( data => ( {
					id: productId,
					title: data?.title?.rendered || __( 'Product', 'at-vehicle-configurator' ),
					price: getPriceFromProduct( data ),
				} ) )
				.catch( () => ( {
					id: productId,
					title: __( 'Product', 'at-vehicle-configurator' ),
					price: 0,
				} ) )
		);

		Promise.all( productPromises )
			.then( results => {
				if ( cancelled ) {
					return;
				}
				setProductsData( results );
				setTotalPrice( results.reduce( ( acc, product ) => acc + product.price, 0 ) );
			} )
			.catch( () => {
				if ( cancelled ) {
					return;
				}
				setProductsData( [] );
				setTotalPrice( 0 );
			} );

		return () => {
			cancelled = true;
		};
	}, [ bundleData ] );

	const handleAddClick = () => {
		onSelect();

		if ( ! bundleData?.acf ) {
			return;
		}

		const baseModelIndex = bundleData.acf.base_model_index;
		const vehicleConfigId = bundleData.acf.vehicle_config;
		const selectedOptionals = bundleData.acf.selected_optionals || [];

		let baseModelId = null;

		// Convert baseModelIndex to number in case it's a string from REST API
		const index = parseInt( baseModelIndex, 10 );
		if (
			! isNaN( index ) &&
			index >= 0 &&
			vehicleConfigId &&
			config.baseModels &&
			config.baseModels[ index ]
		) {
			const baseModel = config.baseModels[ index ];
			// Extract product_id from baseModel (could be object or just the ID)
			baseModelId = typeof baseModel === 'object' ? baseModel.product_id : baseModel;
		}

		onAutoSelect( baseModelId, selectedOptionals );
	};

	const handleRemoveClick = () => {
		if ( onDeselect ) {
			const productIds = bundleData?.acf?.selected_optionals || [];
			onDeselect( productIds );
		}
	};

	const bundleTitle = decodeHtmlEntities( bundleData.title?.rendered ) || 'Bundle';

	return (
		<div className={ `configurator-bundle-card ${ isSelected ? 'selected' : '' }` }>
			{ featuredImageUrl && (
				<div className="configurator-bundle-card-image">
					<img
						src={ featuredImageUrl }
						alt={ bundleTitle }
						loading="lazy"
						decoding="async"
					/>
				</div>
			) }
			<div className="configurator-bundle-card-content">
				<div className="configurator-bundle-card-header">
					<h3 className="configurator-bundle-card-title">{ bundleTitle }</h3>
					{ totalPrice > 0 && (
						<div className="configurator-bundle-card-price">{ formatPrice( totalPrice ) }</div>
					) }
				</div>
				{ productsData.length > 0 && (
					<ul className="configurator-bundle-card-products">
						{ productsData.map( product => (
							<li key={ product.id }>{ decodeHtmlEntities( product.title ) }</li>
						) ) }
					</ul>
				) }
				<div className="configurator-bundle-card-actions">
					{ isSelected ? (
						<button
							type="button"
							className="configurator-button configurator-button-secondary configurator-bundle-card-remove"
							onClick={ handleRemoveClick }
							aria-label={ `${ __( 'Remove', 'at-vehicle-configurator' ) } ${ bundleTitle }` }
						>
							<span className="configurator-button-icon">−</span>
							{ __( 'Remove', 'at-vehicle-configurator' ) }
						</button>
					) : (
						<button
							type="button"
							className="configurator-button configurator-button-primary configurator-bundle-card-add"
							onClick={ handleAddClick }
							aria-label={ `${ __( 'Add', 'at-vehicle-configurator' ) } ${ bundleTitle }` }
						>
							<span className="configurator-button-icon">+</span>
							{ __( 'Add', 'at-vehicle-configurator' ) }
						</button>
					) }
				</div>
			</div>
		</div>
	);
};

BundleCard.propTypes = {
	bundleData: PropTypes.shape( {
		id: PropTypes.number,
		title: PropTypes.shape( { rendered: PropTypes.string } ),
		featured_media_url: PropTypes.string,
		acf: PropTypes.object,
	} ).isRequired,
	config: PropTypes.shape( {
		baseModels: PropTypes.array,
		vehicleConfigId: PropTypes.number,
	} ).isRequired,
	isSelected: PropTypes.bool.isRequired,
	onSelect: PropTypes.func.isRequired,
	onDeselect: PropTypes.func,
	onAutoSelect: PropTypes.func.isRequired,
};

BundleCard.defaultProps = {
	onDeselect: null,
};

export default memo( BundleCard );
